import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { throttleTime } from 'rxjs/operators';

import type { SubscriptionState } from '../admin/plans.types';
import { ServerPlanLock } from '../auth/server-plan-lock';
import { SupabaseService } from '../auth/supabase.service';
import { PermissionsService } from './permissions.service';
import { planNotice } from './plan-expiry.util';

/** A CRM plan as shown on the "no plan" gate screen (read-only catalog entry). */
export interface AvailablePlan {
  readonly id: number;
  readonly tier_name: string;
  readonly price: number;
  readonly billing_period: string;
  readonly modules: string[];
}

/** The clinic's current (or last) CRM subscription, as the /plans page shows it to the owner. */
export interface PlanSummary {
  readonly planName: string;
  /** CRM_PLAN_MODULES keys. */
  readonly modules: readonly string[];
  /** Date-only; null when the subscription is open-ended. */
  readonly endDate: string | null;
  /** Timestamp of the cancellation; null unless cancelled. */
  readonly canceledAt: string | null;
}

/** How often an open CRM tab re-checks the plan, so a renewal or a lapse shows without signing in again. */
const REFRESH_MS = 15 * 60_000;
/** At most one re-read per this long, however many calls the server refuses with plan_locked. */
export const SERVER_LOCK_REREAD_MS = 10_000;

interface MyPlanPayload {
  /** Null when the branch has no CRM subscription (databases before 2026-10-26 sent no payload at all). */
  readonly subscription?: {
    readonly status?: string;
    readonly end_date?: string | null;
    readonly canceled_at?: string | null;
  } | null;
  readonly plan?: { readonly tier_name?: string; readonly modules?: string[] | null } | null;
  /** From the shared lifecycle (public.plan_access_state); absent on databases before 2026-10-19. */
  readonly state?: SubscriptionState;
  readonly blocked?: boolean;
  readonly grace_until?: string | null;
  /** CRM enforcement switch (Super admin › Plans); absent before 2026-10-26. */
  readonly enforced?: boolean;
  /** users_tb.user_type_id = 2; absent before 2026-10-26, when nobody is treated as the owner. */
  readonly is_owner?: boolean;
}

/** Same keys, ignoring order. null (no plan loaded) only equals null. */
function sameModules(a: ReadonlySet<string> | null, b: ReadonlySet<string> | null): boolean {
  if (a === null || b === null) return a === b;
  return a.size === b.size && [...a].every((k) => b.has(k));
}

/**
 * Tracks whether the signed-in user's clinic (home branch) may use the CRM.
 * Loaded at session start (AuthService) so route guards can read it
 * synchronously, then re-checked on window focus and every 15 minutes.
 *
 * Access is crm.get_my_plan().blocked — the same rule crm.my_permissions()
 * uses: trial, active and grace are in; no plan and cancelled are out; a plan
 * past its end date is out only once CRM enforcement is switched on (Super
 * admin › Plans). When access is lost the app moves to the /plans gate.
 *
 * A FAILED CHECK IS NOT AN ANSWER. Once the plan has loaded, an error (a network
 * blip on window focus, a deploy restarting the API) keeps what was last known:
 * treating it as "no plan" used to throw active clinics out to /plans. Only the
 * very first check can fail with nothing to fall back on; that sets loadFailed
 * and the /plans page offers to check again instead of claiming the plan lapsed.
 *
 * Also re-checked when the server refuses an RPC or edge function with
 * plan_locked (HTTP 402, see ServerPlanLock): the last read is stale then.
 */
@Injectable({ providedIn: 'root' })
export class PlanAccessService {
  private readonly supabase = inject(SupabaseService).client;
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsService);

  private readonly _hasActivePlan = signal(false);
  readonly hasActivePlan = this._hasActivePlan.asReadonly();

  private readonly _state = signal<SubscriptionState | null>(null);
  /** none | trial | active | grace | expired | cancelled; null until loaded. */
  readonly state = this._state.asReadonly();

  private readonly _subscription = signal<PlanSummary | null>(null);
  /** Null when the clinic has no CRM subscription, or before the first load. */
  readonly subscription = this._subscription.asReadonly();

  private readonly _graceUntil = signal<string | null>(null);
  /** The last day of grace access, date-only. Null outside grace. */
  readonly graceUntil = this._graceUntil.asReadonly();

  private readonly _isOwner = signal(false);
  /** The clinic owner — the one person shown how to renew. The CRM has no other owner signal. */
  readonly isOwner = this._isOwner.asReadonly();

  private readonly _enforced = signal(false);
  /** Whether CRM enforcement is on, i.e. an expired plan actually locks the clinic out. */
  readonly enforced = this._enforced.asReadonly();

  private readonly _loadFailed = signal(false);
  /** The first check of this session failed, so nothing is known about the plan yet. */
  readonly loadFailed = this._loadFailed.asReadonly();

  /** The plan's module keys (CRM_PLAN_MODULES); null = not loaded, or a super admin (no plan). */
  private readonly _modules = signal<ReadonlySet<string> | null>(null);

  /**
   * What the site-wide banner (shared/plan-expiry-banner) says, if anything —
   * who hears what is decided by planNotice(), shared with the clinic app's
   * rules. Null when there is nothing to say to this user.
   */
  readonly notice = computed(() =>
    planNotice({
      state: this._state(),
      endDate: this._subscription()?.endDate,
      graceUntil: this._graceUntil(),
      isOwner: this._isOwner(),
      enforced: this._enforced(),
    }),
  );

  /** Whether a check has succeeded since sign-in; until then an error has nothing to keep. */
  private loaded = false;
  private watching = false;

  constructor() {
    // A call answered 402: the server locked this clinic, so the plan read is stale. Re-reading it
    // lets followAccess move the user to /plans instead of leaving them on an error. Throttled: a
    // page firing several calls re-reads once.
    inject(ServerPlanLock)
      .locked$.pipe(throttleTime(SERVER_LOCK_REREAD_MS), takeUntilDestroyed())
      .subscribe(() => void this.refreshIfSignedIn());
  }

  async refresh(): Promise<void> {
    const had = this._hasActivePlan();
    const hadModules = this._modules();
    const recovering = this._loadFailed();

    let plan: MyPlanPayload | null;
    try {
      // Via crm.get_my_plan(), not a plain table select: crm.clinic_subscriptions has a
      // second permissive RLS policy granting full-table read to anyone holding
      // admin.clinics.manage, so a plain select can return every clinic's rows for those
      // accounts (Postgres ORs permissive policies together). The RPC is SECURITY DEFINER
      // and filters explicitly by the caller's own branch.
      const { data, error } = await this.supabase.schema('crm').rpc('get_my_plan');
      if (error) throw error;
      plan = data as MyPlanPayload | null;
    } catch {
      // Keep the last answer and stay where we are — see the class docblock.
      if (!this.loaded) this._loadFailed.set(true);
      this.watch();
      return;
    }

    this.apply(plan);
    const firstLoad = !this.loaded;
    this.loaded = true;
    this._loadFailed.set(false);

    // my_permissions() strips everything from a blocked clinic and whatever its
    // plan leaves out, so the permission set is stale the moment access changes.
    // Re-read it BEFORE moving: crmAccessGuard checks it on the way into the
    // app, and a reactivated clinic used to land on /no-access until a reload.
    // At sign-in AuthService has just read it, so the first load skips this —
    // unless that first load had failed, when access may have changed since.
    const accessChanged = had !== this._hasActivePlan() || !sameModules(hadModules, this._modules());
    if (accessChanged && (!firstLoad || recovering)) {
      await this.permissions.refresh();
    }

    this.followAccess(had);
    this.watch();
  }

  /** A null payload means the user has no branch; it reads like a clinic without a plan. */
  private apply(plan: MyPlanPayload | null): void {
    const sub = plan?.subscription ?? null;
    const status = sub?.status;
    const allowed =
      plan !== null && (typeof plan.blocked === 'boolean' ? !plan.blocked : status === 'trial' || status === 'active');
    this._hasActivePlan.set(allowed);
    this._state.set(plan ? (plan.state ?? null) : 'none');
    this._subscription.set(
      sub
        ? {
            planName: plan?.plan?.tier_name ?? '',
            modules: plan?.plan?.modules ?? [],
            endDate: sub.end_date ?? null,
            canceledAt: sub.canceled_at ?? null,
          }
        : null,
    );
    this._graceUntil.set(plan?.grace_until ?? null);
    this._isOwner.set(plan?.is_owner === true);
    this._enforced.set(plan?.enforced === true);
    this._modules.set(plan?.plan?.modules ? new Set(plan.plan.modules) : null);
  }

  /**
   * Leaves the app for the gate when access is lost mid-session, and back when
   * it returns. Owner and staff both go to /plans; the page shows each their own
   * message.
   */
  private followAccess(had: boolean): void {
    const now = this._hasActivePlan();
    if (had === now) return;
    const onGate = this.router.url.startsWith('/plans');
    if (!now && !onGate && had) void this.router.navigateByUrl('/plans');
    if (now && onGate) void this.router.navigateByUrl('/dashboard');
  }

  private watch(): void {
    if (this.watching || typeof window === 'undefined') return;
    this.watching = true;
    window.addEventListener('focus', () => void this.refreshIfSignedIn());
    setInterval(() => void this.refreshIfSignedIn(), REFRESH_MS);
  }

  private async refreshIfSignedIn(): Promise<void> {
    const { data } = await this.supabase.auth.getSession();
    if (data.session) await this.refresh();
  }

  /** All CRM plans for the gate screen — readable by any authenticated user (plans_read_authenticated). */
  async listAvailablePlans(): Promise<AvailablePlan[]> {
    const { data, error } = await this.supabase
      .schema('crm')
      .from('plans')
      .select('id, tier_name, price, billing_period, modules')
      .eq('is_default', true)
      .is('archived_at', null)
      .order('price', { ascending: true });

    if (error) {
      throw error;
    }

    return (data ?? []) as AvailablePlan[];
  }

  /**
   * Whether the clinic's plan includes a CRM module (a CRM_PLAN_MODULES key).
   * For choices about the clinic rather than the user — e.g. which steps the
   * workflow builder offers. Permission checks (PermissionsService.can) already
   * combine role and plan; use those for what a user may open or do.
   * True when no plan is loaded (super admins have none).
   */
  hasModule(key: string): boolean {
    const modules = this._modules();
    return modules === null || modules.has(key);
  }

  /** On sign-out, so the next account in this tab starts from nothing (and from a first load). */
  clear(): void {
    this.loaded = false;
    this._hasActivePlan.set(false);
    this._modules.set(null);
    this._state.set(null);
    this._subscription.set(null);
    this._graceUntil.set(null);
    this._isOwner.set(false);
    this._enforced.set(false);
    this._loadFailed.set(false);
  }
}
